import {
  individualAssuranceAuthenticatorVerify,
  individualAssuranceIdProofingSessionStart,
  individualAssuranceSendOtpByEmail,
  individualAssuranceSendOtpBySms,
  individualAssuranceVerifyOtp,
} from '@api/selfServiceServices';
import {
  type ChannelInfo,
  FactorType,
  type IdProofingSessionStartResponse,
  type VerificationChannels,
} from '@api/types';
import Stepper, { type StepperStep } from '@components/stepper/Stepper';
import { ErrorCode } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { useSingleFlight } from '@hooks/useSingleFlight';
import AuthMethodLayout from '@selfservice/containers/AuthMethods/AuthMethodLayout';
import { openIdProofingPopup } from '@selfservice/utils/idProofingPopup';
import { isObject } from '@utils/utils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EpcsIndividualChannel from './steps/EpcsIndividualChannel';
import EpcsIndividualPassword from './steps/EpcsIndividualPassword';
import EpcsIndividualVerifyIdentity from './steps/EpcsIndividualVerifyIdentity';
import EpcsVerifyAuthenticator from './steps/EpcsVerifyAuthenticator';

export enum IndividualStep {
  EMAIL = 'email',
  PHONE = 'phone',
  PASSWORD = 'password',
  IMPRIVATA_ID = 'imprivata-id',
  IDENTITY = 'identity',
}

/** The two steps that verify a contact channel with a one-time code. */
export type ChannelStep = IndividualStep.EMAIL | IndividualStep.PHONE;

export type OtpSent = { destination: string; otpId: string };

/** A channel step without `otp` still needs a destination from the clinician. */
export type ChannelState = { step: ChannelStep; otp?: OtpSent; codeRejections?: number };

export type IndividualState =
  | ChannelState
  | { step: IndividualStep.PASSWORD; passwordRejections?: number }
  | { step: IndividualStep.IMPRIVATA_ID }
  | { step: IndividualStep.IDENTITY; session?: IdProofingSessionStartResponse };

export type AssuranceContext = { assuranceId: string; workflowId: string; tenantId?: string };

const sendOtp = (channel: ChannelStep, destination: string, ctx: AssuranceContext) =>
  channel === IndividualStep.EMAIL
    ? individualAssuranceSendOtpByEmail(
        { assuranceId: ctx.assuranceId, emailAddress: destination },
        ctx.workflowId,
        ctx.tenantId,
      )
    : individualAssuranceSendOtpBySms(
        { assuranceId: ctx.assuranceId, mobileNumber: destination },
        ctx.workflowId,
        ctx.tenantId,
      );

/**
 * Resolves the state for entering a channel step. A channel the tenant already holds gets its code
 * sent here, in the transition, so the code form never mounts without an `otpId` to verify against.
 */
export const enterChannelStep = async (
  channel: ChannelStep,
  held: ChannelInfo,
  ctx: AssuranceContext,
): Promise<IndividualState> => {
  if (!held.value) {
    return { step: channel };
  }
  const { otpId } = await sendOtp(channel, held.value, ctx);
  return { step: channel, otp: { destination: held.value, otpId } };
};

const isCredentialsRejection = (error: unknown) =>
  isObject(error) && error.code === ErrorCode.INVALID_CREDENTIALS;

const REJECTED_CODE_ERRORS: readonly string[] = [ErrorCode.INVALID_OTP, ErrorCode.OTP_EXPIRED];

/** A wrong and an expired code are one state to the clinician: ask for a new one. */
const isCodeRejection = (error: unknown) =>
  isObject(error) && typeof error.code === 'string' && REJECTED_CODE_ERRORS.includes(error.code);

const STEPPER_ORDER = [
  IndividualStep.EMAIL,
  IndividualStep.PHONE,
  IndividualStep.PASSWORD,
  IndividualStep.IMPRIVATA_ID,
  IndividualStep.IDENTITY,
] as const;

const TRANSLATION = 'self-service.epcs-setup.individual';

interface EpcsIndividualFlowProps {
  assuranceId: string;
  channels: VerificationChannels;
  initialState: IndividualState;
  authenticatorId: string;
  authenticatorShortName: string;
  authenticatorDeviceModel?: string;
  tenantId?: string;
  workflowId: string;
  onStepChange?: (step: IndividualStep) => void;
  cancelFlow: () => void;
  completeFlow: () => void;
}

const EpcsIndividualFlow = ({
  assuranceId,
  channels,
  initialState,
  authenticatorId,
  authenticatorShortName,
  authenticatorDeviceModel,
  tenantId,
  workflowId,
  onStepChange,
  cancelFlow,
  completeFlow,
}: EpcsIndividualFlowProps) => {
  const { t } = useTranslation();
  const { emitError } = useErrorContext();
  const [state, setState] = useState<IndividualState>(initialState);

  const ctx = useMemo<AssuranceContext>(
    () => ({ assuranceId, workflowId, tenantId }),
    [assuranceId, workflowId, tenantId],
  );

  const applyTransition = useCallback(
    async (transition: () => Promise<IndividualState>) => {
      try {
        const next = await transition();
        setState(next);
        onStepChange?.(next.step);
      } catch (error) {
        emitError(new AppError(error as ApiError));
      }
    },
    [emitError, onStepChange],
  );

  // Every step submits through here, so guarding it once stops any of them from sending a code,
  // verifying one, or opening a proofing session twice.
  const [advance, busy] = useSingleFlight(applyTransition);

  /**
   * The email code is spent the moment it verifies, so a failed SMS send must not fail back onto
   * the email step: its code can never verify again. When the tenant holds a phone number and the
   * SMS send fails, the flow is cancelled — the clinician cannot substitute a different number for
   * a held channel. The error is rethrown so applyTransition emits it and skips setState, keeping
   * the component unmounted. Only an unheld channel degrades to the destination form.
   */
  const enterPhoneStep = useCallback(async (): Promise<IndividualState> => {
    try {
      return await enterChannelStep(IndividualStep.PHONE, channels.phone, ctx);
    } catch (error) {
      if (channels.phone.value) {
        cancelFlow();
        throw error;
      }
      emitError(new AppError(error as ApiError));
      return { step: IndividualStep.PHONE };
    }
  }, [channels.phone, ctx, emitError, cancelFlow]);

  const submitDestination = useCallback(
    (channel: ChannelStep, destination: string) =>
      advance(async () => {
        const { otpId } = await sendOtp(channel, destination, ctx);
        return { step: channel, otp: { destination, otpId } };
      }),
    [advance, ctx],
  );

  const resendCode = useCallback(
    (channel: ChannelStep, destination: string) =>
      advance(async () => {
        const { otpId } = await sendOtp(channel, destination, ctx);
        return { step: channel, otp: { destination, otpId } };
      }),
    [advance, ctx],
  );

  const submitCode = useCallback(
    (channel: ChannelStep, otp: OtpSent, code: string, rejections: number) =>
      advance(async () => {
        try {
          await individualAssuranceVerifyOtp(
            ctx.assuranceId,
            otp.otpId,
            code,
            ctx.workflowId,
            ctx.tenantId,
          );
        } catch (error) {
          if (isCodeRejection(error)) {
            return { step: channel, otp, codeRejections: rejections + 1 };
          }
          throw error;
        }
        return channel === IndividualStep.EMAIL
          ? enterPhoneStep()
          : { step: IndividualStep.PASSWORD };
      }),
    [advance, ctx, enterPhoneStep],
  );

  const submitPassword = useCallback(
    (password: string, rejections: number) =>
      advance(async () => {
        try {
          await individualAssuranceAuthenticatorVerify(
            ctx.assuranceId,
            { factorType: FactorType.PASSWORD, password },
            ctx.workflowId,
            ctx.tenantId,
          );
        } catch (error) {
          if (isCredentialsRejection(error)) {
            return { step: IndividualStep.PASSWORD, passwordRejections: rejections + 1 };
          }
          throw error;
        }
        return { step: IndividualStep.IMPRIVATA_ID };
      }),
    [advance, ctx],
  );

  const startIdProofing = useCallback(
    () =>
      advance(async () => {
        // Must open before the await (popup blockers) and before the session exists (a blocked popup must not orphan one).
        const popup = openIdProofingPopup();
        if (!popup) {
          emitError(new AppError({ code: ErrorCode.ID_PROOFING_POPUP_BLOCKED }));
          return { step: IndividualStep.IDENTITY };
        }
        let session: IdProofingSessionStartResponse;
        try {
          session = await individualAssuranceIdProofingSessionStart(
            { assuranceId: ctx.assuranceId },
            ctx.workflowId,
            ctx.tenantId,
          );
          if (new URL(session.url).protocol !== 'https:') {
            throw new AppError({ code: ErrorCode.ID_PROOFING_FAILED });
          }
        } catch (error) {
          popup.close();
          throw error;
        }
        popup.location.href = session.url;
        return { step: IndividualStep.IDENTITY, session };
      }),
    [advance, ctx, emitError],
  );

  const stepperSteps: StepperStep<IndividualStep>[] = STEPPER_ORDER.map(key => ({
    key,
    label: t(`${TRANSLATION}.stepper.${key}`),
  }));

  const renderStep = () => {
    switch (state.step) {
      case IndividualStep.EMAIL:
      case IndividualStep.PHONE:
        return (
          <EpcsIndividualChannel
            channel={state.step}
            otp={state.otp}
            busy={busy}
            onDestination={destination => submitDestination(state.step, destination)}
            codeRejections={state.codeRejections ?? 0}
            onCode={(otp, code) => submitCode(state.step, otp, code, state.codeRejections ?? 0)}
            onResend={() => state.otp && resendCode(state.step, state.otp.destination)}
            cancelFlow={cancelFlow}
          />
        );
      case IndividualStep.PASSWORD:
        return (
          <EpcsIndividualPassword
            busy={busy}
            rejections={state.passwordRejections ?? 0}
            onSubmit={password => submitPassword(password, state.passwordRejections ?? 0)}
            cancelFlow={cancelFlow}
          />
        );
      case IndividualStep.IMPRIVATA_ID:
        return (
          <EpcsVerifyAuthenticator
            authenticatorShortName={authenticatorShortName}
            authenticatorDeviceModel={authenticatorDeviceModel}
            verify={tokenCode =>
              individualAssuranceAuthenticatorVerify(
                assuranceId,
                { factorType: FactorType.IMPRIVATA_ID, authenticatorId, tokenCode },
                workflowId,
                tenantId,
              )
            }
            onVerified={() => {
              setState({ step: IndividualStep.IDENTITY });
              onStepChange?.(IndividualStep.IDENTITY);
            }}
            cancelFlow={cancelFlow}
          />
        );
      case IndividualStep.IDENTITY:
        return (
          <EpcsIndividualVerifyIdentity
            session={state.session}
            ctx={ctx}
            busy={busy}
            onStart={startIdProofing}
            cancelFlow={cancelFlow}
            completeFlow={completeFlow}
          />
        );
    }
  };

  return (
    <AuthMethodLayout
      graphic={
        <div className="epcs-stepper-panel">
          <Stepper steps={stepperSteps} current={state.step} />
        </div>
      }
    >
      {renderStep()}
    </AuthMethodLayout>
  );
};

export default EpcsIndividualFlow;
